import React from 'react'

import { XMarkIcon } from '@heroicons/react/24/outline'

import { PermissionGuard } from '../../components/PermissionGuard'
import { Button, Heading, Text, LoadingSpinner } from '../../components/ui'
import { useAuthContext } from '../../contexts/AuthContext'
import {
  comercialCotacoesService,
  getBrokerRazao,
  QUOTE_OBSERVATION_MAX_USER_LENGTH,
} from '../../services/comercialCotacoes'
import type {
  BrokerQuoteMaps,
  HelpdeskQuote,
  HelpdeskQuoteObs,
  HelpdeskQuoteObservation,
  HelpdeskQuoteStatusHistory,
} from '../../types/comercialCotacoes'
import {
  formatCommission,
  formatCurrencyBRL,
  formatDateTimePtBr,
  formatIntegerPtBr,
  formatPhoneBR,
  formatQuoteLabel,
  formatResponsibleDisplayName,
  quoteStatusBadgeClass,
} from '../../utils/cotacaoFormatters'

type DetailTab = 'detalhes' | 'historico'

const tabClass = (activeTab: DetailTab, tab: DetailTab) =>
  `py-3 px-1 border-b-2 font-medium text-sm ${
    activeTab === tab
      ? 'border-primary-600 text-primary-600 dark:text-primary-400'
      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200'
  }`

type CotacaoDetailModalProps = {
  quote: HelpdeskQuote
  brokerMaps: BrokerQuoteMaps
  /** Nome do comercial da corretora (broker_comercial_assignments); já resolvido pela página. */
  comercialResponsavelNome?: string | null
  /** Nome do consultor da corretora (broker_consultant_assignments); já resolvido pela página. */
  consultantResponsavelNome?: string | null
  onClose: () => void
  onDownloadPdf: (quote: HelpdeskQuote, source?: 'relacao' | 'modal') => Promise<void>
  pdfLoading: boolean
  canDownloadInfo?: boolean
}

const DetailField: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div>
    <Text size="xs" color="muted" className="uppercase tracking-wider">
      {label}
    </Text>
    <Text className="mt-1">{value}</Text>
  </div>
)

const CotacaoDetailModal: React.FC<CotacaoDetailModalProps> = ({
  quote,
  brokerMaps,
  comercialResponsavelNome = null,
  consultantResponsavelNome = null,
  onClose,
  onDownloadPdf,
  pdfLoading,
  canDownloadInfo = false,
}) => {
  const { profile } = useAuthContext()
  const [lastObservation, setLastObservation] = React.useState<HelpdeskQuoteObservation | null>(null)
  const [observationLoading, setObservationLoading] = React.useState(true)
  const [wizComments, setWizComments] = React.useState<HelpdeskQuoteObs[]>([])
  const [wizCommentsLoading, setWizCommentsLoading] = React.useState(true)
  const [novaObservacao, setNovaObservacao] = React.useState('')
  const [submitting, setSubmitting] = React.useState(false)
  const [formMessage, setFormMessage] = React.useState<{
    type: 'success' | 'error'
    text: string
  } | null>(null)
  const [activeTab, setActiveTab] = React.useState<DetailTab>('detalhes')
  const [statusHistory, setStatusHistory] = React.useState<HelpdeskQuoteStatusHistory[]>([])
  const [statusHistoryLoading, setStatusHistoryLoading] = React.useState(false)
  const [statusHistoryError, setStatusHistoryError] = React.useState<string | null>(null)
  const [statusHistoryLoaded, setStatusHistoryLoaded] = React.useState(false)

  const brokerLabel = getBrokerRazao(quote.broker_id, brokerMaps)
  const codUsuarioScgp = profile?.id_scgp ?? null
  const hasScgpCode = codUsuarioScgp != null && Number.isFinite(Number(codUsuarioScgp)) && Number(codUsuarioScgp) >= 1

  const loadLastObservation = React.useCallback(async () => {
    setObservationLoading(true)
    try {
      const observation = await comercialCotacoesService.getLastObservation(quote.id)
      setLastObservation(observation)
    } catch (error) {
      console.error('Erro ao carregar observação da cotação:', error)
      setLastObservation(null)
    } finally {
      setObservationLoading(false)
    }
  }, [quote.id])

  const loadWizComments = React.useCallback(async () => {
    setWizCommentsLoading(true)
    try {
      const comments = await comercialCotacoesService.listQuoteObs(quote.id)
      setWizComments(comments)
    } catch (error) {
      console.error('Erro ao carregar comentários Wiz da cotação:', error)
      setWizComments([])
    } finally {
      setWizCommentsLoading(false)
    }
  }, [quote.id])

  React.useEffect(() => {
    void loadLastObservation()
  }, [loadLastObservation])

  React.useEffect(() => {
    void loadWizComments()
  }, [loadWizComments])

  const loadStatusHistory = React.useCallback(async () => {
    setStatusHistoryLoading(true)
    setStatusHistoryError(null)
    try {
      const rows = await comercialCotacoesService.listQuoteStatusHistory(quote.id)
      setStatusHistory(rows)
      setStatusHistoryLoaded(true)
    } catch (error) {
      console.error('Erro ao carregar histórico de status da cotação:', error)
      setStatusHistory([])
      const text =
        error instanceof Error ? error.message : 'Não foi possível carregar o histórico de status.'
      setStatusHistoryError(text)
    } finally {
      setStatusHistoryLoading(false)
    }
  }, [quote.id])

  React.useEffect(() => {
    if (activeTab !== 'historico' || statusHistoryLoaded) return
    void loadStatusHistory()
  }, [activeTab, statusHistoryLoaded, loadStatusHistory])

  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !submitting) onClose()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [onClose, submitting])

  const observationDisplay = React.useMemo(() => {
    if (observationLoading) return 'Carregando...'
    if (!lastObservation) return '—'

    const parts = [
      lastObservation.author_name?.trim(),
      lastObservation.noted_at
        ? formatDateTimePtBr(lastObservation.noted_at)
        : formatDateTimePtBr(lastObservation.created_at),
      lastObservation.note_text?.trim(),
    ].filter(Boolean)

    return parts.length > 0 ? parts.join(' — ') : '—'
  }, [lastObservation, observationLoading])

  const handleSaveObservation = async () => {
    if (!hasScgpCode || codUsuarioScgp == null) return

    setFormMessage(null)
    setSubmitting(true)
    try {
      await comercialCotacoesService.insertQuoteObservation({
        quoteId: quote.id,
        observacao: novaObservacao,
        codUsuario: codUsuarioScgp,
      })
      setNovaObservacao('')
      await Promise.all([loadLastObservation(), loadWizComments()])
      setFormMessage({ type: 'success', text: 'Observação registrada com sucesso.' })
    } catch (error) {
      const text =
        error instanceof Error ? error.message : 'Não foi possível registrar a observação.'
      setFormMessage({ type: 'error', text })
    } finally {
      setSubmitting(false)
    }
  }

  const canSubmitObservation =
    hasScgpCode && novaObservacao.trim().length > 0 && !submitting

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4 text-center">
        <div className="fixed inset-0 bg-black/40" onClick={onClose} aria-hidden="true" />
        <div
          className="relative w-full max-w-3xl rounded-lg bg-white p-6 text-left shadow-xl dark:bg-gray-800 max-h-[90vh] overflow-y-auto"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cotacao-detail-title"
        >
          <div className="flex items-start justify-between gap-4 border-b border-gray-200 pb-4 dark:border-gray-700">
            <div className="min-w-0">
              <Heading id="cotacao-detail-title" as="h2" size="2xl" className="text-gray-900 dark:text-white">
                Cotação {formatQuoteLabel(quote.quote_number, quote.version)}
              </Heading>
              <Text size="sm" color="muted" className="mt-1">
                {brokerLabel}
              </Text>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {canDownloadInfo && (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pdfLoading}
                  onClick={() => void onDownloadPdf(quote, 'modal')}
                >
                  {pdfLoading ? (
                    <span className="inline-flex items-center gap-2">
                      <LoadingSpinner size="sm" />
                      Baixando...
                    </span>
                  ) : (
                    'Baixar cotação'
                  )}
                </Button>
              )}
              <button
                type="button"
                onClick={onClose}
                className="rounded-md p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200"
                aria-label="Fechar"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          </div>

          <div className="mt-4 border-b border-gray-200 dark:border-gray-700">
            <nav className="-mb-px flex space-x-6" aria-label="Seções da cotação">
              <button
                type="button"
                onClick={() => setActiveTab('detalhes')}
                className={tabClass(activeTab, 'detalhes')}
              >
                Detalhes
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('historico')}
                className={tabClass(activeTab, 'historico')}
              >
                Histórico de status
              </button>
            </nav>
          </div>

          {activeTab === 'detalhes' && (
          <>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <DetailField
              label="Comercial responsável corretor"
              value={comercialResponsavelNome?.trim() || '—'}
            />
            <DetailField
              label="Consultor responsável corretor"
              value={consultantResponsavelNome?.trim() || '—'}
            />
            <DetailField label="Responsável cotação" value={quote.responsible?.trim() || '—'} />
            <DetailField label="Empresa" value={quote.company?.trim() || '—'} />
            <DetailField label="UF" value={quote.state_code?.trim() || '—'} />
            <DetailField label="Cidade" value={quote.city?.trim() || '—'} />
            <DetailField
              label="Status"
              value={
                quote.status ? (
                  <span
                    className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold border ${quoteStatusBadgeClass(quote.status)}`}
                  >
                    {quote.status}
                  </span>
                ) : (
                  '—'
                )
              }
            />
            <DetailField label="Data de inclusão" value={formatDateTimePtBr(quote.inclusion_at)} />
            <DetailField label="Quantidade de vidas" value={formatIntegerPtBr(quote.insured_count)} />
            <DetailField label="Nome do solicitante" value={quote.requester_name?.trim() || '—'} />
            <DetailField label="Telefone do solicitante" value={formatPhoneBR(quote.requester_phone)} />
            <DetailField label="E-mail do solicitante" value={quote.requester_email?.trim() || '—'} />
            <DetailField label="Capital" value={formatCurrencyBRL(quote.amount)} />
            <DetailField label="Valor por vida" value={formatCurrencyBRL(quote.amount_per_life)} />
            <DetailField label="Comissão (%)" value={formatCommission(quote.commission)} />
            <DetailField
              label="Fatura mínima"
              value={formatCurrencyBRL(quote.minimum_incoice_amount ?? null)}
            />
            <DetailField label="Total prêmio" value={formatCurrencyBRL(quote.total_premium ?? null)} />
            <div className="md:col-span-2">
              <DetailField label="Última observação" value={observationDisplay} />
            </div>
          </div>

          <div className="mt-6 border-t border-gray-200 pt-4 dark:border-gray-700">
            <Text size="sm" className="font-medium text-gray-900 dark:text-white mb-2">
              Comentários Wiz
            </Text>
            {wizCommentsLoading ? (
              <div className="py-4 flex justify-center">
                <LoadingSpinner size="sm" />
              </div>
            ) : wizComments.length === 0 ? (
              <Text size="sm" color="muted">
                Nenhum comentário registrado.
              </Text>
            ) : (
              <div className="max-h-56 overflow-y-auto space-y-3 pr-1">
                {wizComments.map((item) => (
                  <div
                    key={item.id}
                    className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-600 dark:bg-gray-700/40"
                  >
                    <Text size="xs" color="muted" className="mb-1">
                      {formatDateTimePtBr(item.created_at)}
                    </Text>
                    <Text size="sm" className="whitespace-pre-wrap break-words text-gray-800 dark:text-gray-100">
                      {item.observation?.trim() || '—'}
                    </Text>
                  </div>
                ))}
              </div>
            )}
          </div>

          <PermissionGuard moduleName="comercial_cotacoes" permissionType="update" fallback={null}>
            <div className="mt-6 border-t border-gray-200 pt-4 dark:border-gray-700">
              <Text size="sm" className="font-medium text-gray-900 dark:text-white mb-2">
                Nova observação
              </Text>
              {!hasScgpCode && (
                <Text size="sm" color="muted" className="mb-3">
                  Seu código SCGP não está cadastrado. Solicite ao administrador para poder registrar
                  observações.
                </Text>
              )}
              <textarea
                value={novaObservacao}
                onChange={(e) => {
                  setNovaObservacao(e.target.value)
                  if (formMessage) setFormMessage(null)
                }}
                placeholder="Digite a observação para esta cotação..."
                className="w-full h-28 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white resize-none disabled:opacity-60"
                maxLength={QUOTE_OBSERVATION_MAX_USER_LENGTH}
                disabled={submitting || !hasScgpCode}
                aria-label="Nova observação"
              />
              <div className="mt-1 flex items-center justify-between gap-2">
                <Text size="xs" color="muted">
                  {novaObservacao.length} / {QUOTE_OBSERVATION_MAX_USER_LENGTH}
                </Text>
              </div>
              {formMessage && (
                <div
                  role="status"
                  className={`mt-3 p-3 rounded-lg text-sm ${
                    formMessage.type === 'success'
                      ? 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400'
                      : 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400'
                  }`}
                >
                  {formMessage.text}
                </div>
              )}
              <div className="mt-4 flex justify-end">
                <Button
                  variant="solid"
                  size="sm"
                  onClick={() => void handleSaveObservation()}
                  disabled={!canSubmitObservation}
                  aria-busy={submitting}
                >
                  {submitting ? (
                    <span className="inline-flex items-center gap-2">
                      <LoadingSpinner size="sm" />
                      Salvando...
                    </span>
                  ) : (
                    'Salvar observação'
                  )}
                </Button>
              </div>
            </div>
          </PermissionGuard>
          </>
          )}

          {activeTab === 'historico' && (
            <div className="mt-4">
              {statusHistoryLoading ? (
                <div className="py-8 flex justify-center">
                  <LoadingSpinner size="sm" />
                </div>
              ) : statusHistoryError ? (
                <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-400">
                  {statusHistoryError}
                </div>
              ) : statusHistory.length === 0 ? (
                <Text size="sm" color="muted">
                  Nenhum histórico de status para esta cotação.
                </Text>
              ) : (
                <div className="overflow-x-auto max-h-80 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700">
                  <table className="w-full min-w-[32rem] divide-y divide-gray-200 text-sm dark:divide-gray-700">
                    <thead className="sticky top-0 bg-gray-50 text-xs uppercase text-gray-500 dark:bg-gray-700 dark:text-gray-400">
                      <tr>
                        <th className="px-4 py-3 text-left font-medium">Data</th>
                        <th className="px-4 py-3 text-left font-medium">Status</th>
                        <th className="px-4 py-3 text-left font-medium">Responsável</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-800">
                      {statusHistory.map((item) => {
                        const responsible = item.responsible?.trim()
                          ? formatResponsibleDisplayName(item.responsible)
                          : '—'
                        return (
                          <tr key={item.id}>
                            <td className="px-4 py-2 whitespace-nowrap text-gray-600 dark:text-gray-300">
                              {formatDateTimePtBr(item.occurred_at)}
                            </td>
                            <td className="px-4 py-2">
                              {item.status_description?.trim() ? (
                                <span
                                  className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${quoteStatusBadgeClass(item.status_description)}`}
                                >
                                  {item.status_description.trim()}
                                </span>
                              ) : (
                                '—'
                              )}
                            </td>
                            <td className="px-4 py-2 text-gray-600 dark:text-gray-300">
                              {responsible}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          <div className="mt-6 flex justify-end border-t border-gray-200 pt-4 dark:border-gray-700">
            <Button variant="ghost" onClick={onClose} disabled={submitting}>
              Fechar
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CotacaoDetailModal
